import { Grid, Input, Stack, Text, Textarea } from '@chakra-ui/react';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { generateTranslatedOptions } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { ChangeEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_FORM_FIELD_VALUE, INPUT_TYPE_OPTIONS } from 'widgets/FormWidget/FormWidget.config';
import { FormType, FormWidgetData } from 'widgets/FormWidget/FormWidget.types';
import { useEditor, useWidget } from 'widgets/sdk';

export const Fields = ({ isLabelFocused = false }) => {
  const { type } = useWidget<FormWidgetData>();
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'formToolbar' });
  const isShortQuestion = type === FormType.ShortQuestion;

  return (
    <Stack spacing={4} px={4}>
      {isShortQuestion && (
        <Grid templateColumns='1fr auto'>
          <Text fontSize='sm' color='font.500' fontWeight='medium'>
            {t('settingsSideBar.fieldType')}
          </Text>
          <InputTypeSelect />
        </Grid>
      )}
      <Grid templateColumns={isShortQuestion ? '1fr auto' : '1fr'} gap={isShortQuestion ? '0' : '8px'}>
        <Text fontSize='sm' color='font.500' fontWeight='medium'>
          {t('labelText')}
        </Text>
        <ValueInput isLabelFocused={isLabelFocused} />
      </Grid>
      <Grid templateColumns='1fr auto'>
        <Text fontSize='sm' color='font.500' fontWeight='medium'>
          {t('placeholder')}
        </Text>
        <PlaceholderInput />
      </Grid>
    </Stack>
  );
};

Fields.displayName = 'Fields';
export default Fields;

const ValueInput = ({ isLabelFocused = false }) => {
  const {
    type,
    field: { label },
    updateWidget,
    isWidgetSelected,
  } = useWidget<FormWidgetData>();
  const { isSideWidgetMenuOpen } = useEditor();
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'formToolbar' });
  const [localLabel, setLocalLabel] = useState(label);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const updateValue = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setLocalLabel(newValue);
      updateWidget({ field: { label: newValue || '' } });
    },
    [updateWidget],
  );

  // Sync local state with the external label state when it changes
  useEffect(() => {
    setLocalLabel(label);
  }, [label]);

  // Highlight the label input text when the label is focused and the side menu is open
  useEffect(() => {
    if (isWidgetSelected && isLabelFocused && inputRef.current && isSideWidgetMenuOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        // Make sure the cursor is at the end of the input
        if (inputRef.current) {
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(0, length);
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isLabelFocused, isWidgetSelected, isSideWidgetMenuOpen]);

  return type === FormType.LongQuestion ? (
    <Textarea
      ref={inputRef as RefObject<HTMLTextAreaElement>}
      resize='none'
      placeholder={''}
      value={localLabel}
      onChange={updateValue}
      borderRadius='base'
      borderColor='chakra-border-color'
      _hover={{
        borderColor: 'gray.300',
        backgroundColor: 'hover.gray',
      }}
      fontSize='sm'
    />
  ) : (
    <Input
      ref={inputRef as RefObject<HTMLInputElement>}
      placeholder={t('name')}
      value={localLabel}
      onChange={updateValue}
      size={'sm'}
      borderRadius='base'
    />
  );
};

const PlaceholderInput = () => {
  const {
    field: { placeholder },
    updateWidget,
  } = useWidget<FormWidgetData>();
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'formToolbar' });

  const updateValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      updateWidget({ field: { placeholder: e.target.value || '' } });
    },
    [updateWidget],
  );
  return (
    <Input placeholder={t('placeholder')} value={placeholder} onChange={updateValue} size={'sm'} borderRadius='base' />
  );
};

const InputTypeSelect = () => {
  const {
    field: { type },
    updateWidget,
  } = useWidget<FormWidgetData>();
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'formToolbar' });
  const selectedIndex = INPUT_TYPE_OPTIONS.findIndex((option) => option.value === type);

  const onUpdateInputType = (type: string) => {
    const { value, placeholder } = DEFAULT_FORM_FIELD_VALUE[type as keyof typeof DEFAULT_FORM_FIELD_VALUE];
    updateWidget({ field: { type, placeholder, value } });
  };

  return (
    <DropdownPopover
      selectedIndex={selectedIndex}
      options={generateTranslatedOptions(t, INPUT_TYPE_OPTIONS, 'settingsSideBar')}
      label={t('settingsSideBar.fieldType')}
      w={'150px'}
      spacing={2}
      onSelect={onUpdateInputType}
      buttonVariant={'toolbar-dropdown-option'}
      optionsButtonVariant={'toolbar-dropdown-item-basic'}
      triggerSelectOnSameValue={false}
      useLabelInsteadOfSelectedLabel
      triggerProps={{
        height: 8,
      }}
      buttonOptionProps={{
        height: 7,
        justifyContent: 'flex-start',
      }}
    />
  );
};
